import { useAsync } from 'react-async-hook'
import { useApiClient, useEventBus } from '../../core/hooks'
import { UIErrorCode } from '../../errors/constants'
import { PlatformError } from '../../errors/utils'
import { DEFAULT_TEMPLATE_NAME } from '../constants'
import { renderTemplate } from '../helpers/nunjucks'
import { parsePlatformTemplate } from '../helpers/parser'
import { useNunjucksEnvironment } from './nunjucks'
import type { TemplateContext } from '../types'

export const getTemplateExtendName = (template: string) => {
  const extendedTemplateTagTest = /{% extends.* %}/gm
  const extended = template.match(extendedTemplateTagTest)

  return extended ? extended[0].split(/('|")/)[2] : undefined
}

/**
 * Returns a React tree parsed from a Nunjucks template.
 *
 * One of `templateName` OR `content` should be provided - never both.
 * `content`
 * can be useful when an extra API request is overkill. `DEFAULT_TEMPLATE_NAME`
 * is used for the provided template content so the loader knows not to fetch
 * from the API.
 *
 * @param options
 * @param options.templateName The name of a known template to fetch and render
 * @param options.content A Nunjucks template string to render
 * @param options.context Template context to be passed into the Nunjucks
 *   environment
 */
export const useTemplate = ({
  templateName: maybeTemplateName,
  content,
  context,
}: {
  templateName?: string
  content?: string
  context?: TemplateContext
}) => {
  const templateName = content ? DEFAULT_TEMPLATE_NAME : maybeTemplateName
  const apiClient = useApiClient()
  const eventBus = useEventBus()
  const { env: nunjucksEnvironment, templateUid } = useNunjucksEnvironment(
    apiClient,
    { templateName, content },
  )
  const { result, loading, error } = useAsync(
    renderTemplate,
    [nunjucksEnvironment, templateName, templateUid, context],
    {
      setLoading: (state) => ({
        ...state,
        loading: true,
        // If only the context has changed, retain the old result whilst loading
        result:
          templateName === state.result?.templateName
            ? state.result
            : undefined,
      }),
    },
  )

  let templateError

  if (!loading && (result || error)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - loaders isn't recognized in the types but it is there,
    // tried updating to @types/nunjucks: 3.2.1, no success.
    const cache = nunjucksEnvironment?.loaders[0].cache
    const templates = Object.keys(cache)

    if (templateName && !error) {
      templates.forEach((loader) => {
        const template = cache[loader]

        eventBus.emit({
          topic: 'horizon:template:loaded:v1',
          payload: {
            name: loader,
            extendedFrom: getTemplateExtendName(template.tmplStr),
          },
        })
      })
    }

    if (error) {
      if (templates.length) {
        templateError = new PlatformError(
          error.message,
          UIErrorCode.TEMPLATE_RENDER_FAILED,
          undefined,
          {
            templateUid,
            error,
          },
        )
      } else {
        templateError = new PlatformError(
          error.message,
          UIErrorCode.TEMPLATE_NOT_FOUND,
        )
      }
    }
  }

  return {
    loading,
    error: templateError,
    result: result ? parsePlatformTemplate(result.result) : null,
  }
}
